import axios from "axios";
import { BASE_URL } from "../Constants/Api";

/**
 * User Notes API (JWT-scoped on the server).
 * Paths are under /notes (not /user/notes) so Spring does not treat "notes" as /api/user/{id}.
 *
 * - GET    /notes?page=&size=&search=&sort=
 * - GET    /notes/:id
 * - POST   /notes  { title, content }
 * - PUT    /notes/:id
 * - DELETE /notes/:id
 * - POST   /notes/:noteId/attachments  (multipart field "file")
 * - DELETE /notes/:noteId/attachments/:attachmentId
 * - GET    /notes/:noteId/attachments/:attachmentId/download
 */

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const normalizeListResponse = (data) => {
  if (!data) {
    return { notes: [], totalElements: 0 };
  }
  if (Array.isArray(data)) {
    return { notes: data, totalElements: data.length };
  }
  const content = data.content ?? data.notes ?? [];
  const total =
    data.totalElements ??
    data.total ??
    (Array.isArray(content) ? content.length : 0);
  return { notes: Array.isArray(content) ? content : [], totalElements: total };
};

export const fetchNotes = async (params = {}) => {
  const { page = 0, size = 20, search = "", sort = "updatedAt,desc" } = params;
  const response = await api.get("/notes", {
    params: {
      page,
      size,
      ...(search ? { search: search.trim() } : {}),
      sort,
    },
  });
  return normalizeListResponse(response.data);
};

export const getNoteById = async (id) => {
  const response = await api.get(`/notes/${id}`);
  return response.data;
};



export const createNote = async ({ title, content }, files = []) => {
  const formData = new FormData();
  formData.append("title", title ?? "");
  formData.append("content", content ?? "");

  (files ?? []).map((file) => {
    if (file) formData.append("files", file);
  });

  const response = await api.post("/notes", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};

/** Update note; optional `files` are new attachments only (multipart). */
export const updateNote = async (id, { title, content }, files = []) => {
  const formData = new FormData();
  formData.append("title", title ?? "");
  formData.append("content", content ?? "");

  (files ?? []).map((file) => {
    if (file) formData.append("files", file);
  });

  const response = await api.put(`/notes/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};

export const deleteNote = async (id) => {
  await api.delete(`/notes/${id}`);
};

export const uploadNoteAttachment = async (noteId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await api.post(`/notes/${noteId}/attachments`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
};

export const deleteNoteAttachment = async (noteId, attachmentId) => {
  await api.delete(`/notes/${noteId}/attachments/${attachmentId}`);
};

/**
 * Download to disk. Uses attachment.downloadUrl if set (relative or absolute), else default download path.
 */
export const downloadNoteAttachment = async (noteId, attachment) => {
  const pathOrUrl =
    attachment.downloadUrl ||
    `/notes/${noteId}/attachments/${attachment.id}/download`;
  const response = await api.get(pathOrUrl, { responseType: "blob" });
  const type =
    response.headers["content-type"] || "application/octet-stream";
  const blob = new Blob([response.data], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = attachment.fileName || "download";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export { api as notesApiClient };
